import { useState } from "react";
import { FormikErrors, useFormik, FormikProvider } from "formik";
import useApi from "system/hooks/use-api";
import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  SelectChangeEvent,
} from "@mui/material";
import { AccessEntryInfo } from "system/types/wireTypes";
import { Role, Roles, RoleLabels } from "system/types/enums/Roles";

interface IProps {
  done: (user: AccessEntryInfo) => void;
  user?: AccessEntryInfo;
  setIsFormSubmitting: (value: boolean) => void;
}

interface ViewModel {
  email: string;
  roles: Role[];
}

export default function AccessForm({ done, user, setIsFormSubmitting }: IProps) {
  const api = useApi();
  const [message, setMessage] = useState("");

  const form = useFormik({
    initialValues: {
      email: user?.email ?? "",
      roles: user?.roles ?? [Roles.User],
    },
    validate: (values) => {
      const errors: FormikErrors<ViewModel> = {};
      if (!values.email) errors.email = "Required";
      if (!values.roles || values.roles.length === 0) errors.roles = "At least one role is required";
      const validRoles = Object.values(Roles) as string[];
      if (values.roles.some((role) => !validRoles.includes(role))) {
        errors.roles = "Invalid role selected";
      }
      return errors;
    },
    onSubmit: async (values) => {
      setIsFormSubmitting(true);
      const userData: AccessEntryInfo = {
        email: user?.email ?? values.email,
        roles: values.roles,
        userId: user?.userId,
        name: user?.name,
        groupId: user?.groupId,
      };

      const result = user ? await api.access.update(userData) : await api.access.grant(userData);
      if (result.isSuccess) {
        const user = result.value
          ? {
              ...result.value,
              roles: userData.roles,
            }
          : userData;
        done(user);
      } else {
        setMessage(result.failMessage ?? "Error");
        setIsFormSubmitting(false);
      }
      form.setSubmitting(false);
    },
  });

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    if (typeof value === "string") return;
    if (value.includes("select-all")) {
      const allRoles = Object.values(Roles);
      form.setFieldValue("roles", form.values.roles.length === allRoles.length ? [] : allRoles);
    } else {
      form.setFieldValue("roles", value);
    }
  };

  return (
    <form id="AccessForm" onSubmit={form.handleSubmit}>
      <FormikProvider value={form}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1em" }}>
          <TextField
            label="User email address"
            name="email"
            value={form.values.email}
            onChange={form.handleChange}
            error={!!form.errors.email}
            helperText={form.errors.email}
            variant="standard"
            InputLabelProps={{ style: { color: "#ffffff" } }}
            InputProps={{
              style: {
                color: !!user ? "#757575" : "#4caf50",
                padding: "6px",
              },
            }}
            disabled={!!user}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#757575 !important",
              },
            }}
          />

          <FormControl fullWidth error={!!form.errors.roles}>
            <InputLabel id="roles-label" sx={{ color: "#ffffff", fontSize: "0.875rem" }}>
              Roles
            </InputLabel>
            <Select
              labelId="roles-label"
              id="roles"
              name="roles"
              multiple
              value={form.values.roles}
              onChange={handleChange}
              renderValue={(selected) => selected.map((role) => RoleLabels[role as Role]).join(", ")}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "#1b1a19",
                    border: "1px solid #757575",
                  },
                },
                MenuListProps: {
                  sx: {
                    "& .MuiMenuItem-root": {
                      backgroundColor: "#1b1a19",
                      color: "#ffffff",
                      "&:hover": {
                        backgroundColor: "#2c2c2c",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#2c2c2c",
                        "&:hover": {
                          backgroundColor: "#3c3c3c",
                        },
                      },
                    },
                  },
                },
              }}
              sx={{
                color: "#ffffff",
                fontSize: "0.875rem",
                ".MuiSelect-icon": { color: "#ffffff" },
                ".MuiOutlinedInput-notchedOutline": { borderColor: "#757575" },
              }}
            >
              <MenuItem
                value="select-all"
                onClick={(e) => {
                  e.preventDefault();
                  const allRoles = Object.values(Roles);
                  form.setFieldValue("roles", form.values.roles.length === allRoles.length ? [] : allRoles);
                }}
              >
                <Checkbox
                  checked={form.values.roles.length === Object.values(Roles).length}
                  indeterminate={form.values.roles.length > 0 && form.values.roles.length < Object.values(Roles).length}
                />
                <ListItemText primary={form.values.roles.length === Object.values(Roles).length ? "Deselect All" : "Select All"} />
              </MenuItem>
              {Object.values(Roles).map((role) => (
                <MenuItem key={role} value={role}>
                  <Checkbox checked={form.values.roles.includes(role)} />
                  <ListItemText primary={RoleLabels[role as Role]} />
                </MenuItem>
              ))}
            </Select>
            {form.errors.roles && <FormHelperText sx={{ color: "#d32f2f" }}>{form.errors.roles}</FormHelperText>}
          </FormControl>
          <div className="warning" style={{ color: "#d32f2f" }}>
            {message}
          </div>
        </Box>
      </FormikProvider>
    </form>
  );
}
